import { OutlinedInput } from '@material-ui/core';
// material-ui
import { Button, Divider, Grid, InputLabel, Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import moment from 'moment';
import React, { ChangeEvent, ReactElement } from 'react';
// @ts-ignore
import XMLViewer from 'react-xml-viewer';

import { getSmallSites } from '~/src/dash/api/channels/channels_methods';
import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import {
  getInventoryRoutesByPublisherID,
  IDestination,
  IInventoryRoute,
} from '~/src/dash/api/inventory_routes';
// api
import { filterLiveLogs } from '~/src/dash/api/live_logs/helpers';
import {
  ILiveStreamWatcherAdBreakData,
  IStreamAdFilledData,
  IStreamPodFilledData,
  IStreamPodRequestData,
} from '~/src/dash/api/live_logs/models';
import {
  GetNetworkChannelLiveLogsData,
  INetworkChannelLiveLogsWrapper,
} from '~/src/dash/api/live_logs/stream';
import {
  demandSourceStringToDemandSourceType,
  INetworkChannel,
  NetworkChannelField,
  NetworkDemandSourceType,
  networkDemandSourceTypeToString,
} from '~/src/dash/api/network';
import InputGridRow from '~/src/dash/components/input-grid-row';
import { LiveLogsWrapper } from '~/src/dash/components/live_logs/live_logs_wrapper';
import { LiveLogsGeneralTable } from '~/src/dash/components/live_logs/live-logs-general-table';
import { LiveContextAdBreakTable } from '~/src/dash/components/logs-live-context-ad-break-table';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { ModelPrimaryContainerV2 } from '~/src/dash/components/model-grid-container-v2';
import PillSwitcher, { IPillItem } from '~/src/dash/components/pill-switcher';
// components
import { VastEditor } from '~/src/dash/components/vast-editor';
// other
import { IStore } from '~/src/dash/reducers/store';
// views
import { IMenuList } from '~/src/dash/types/menuList';
import {
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { formatTime } from '~/src/dash/utils/date';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { smallRadius } from '~/src/dash/utils/material-dashboard-react';
import { checkUserInfoMasked, isSuper } from '~/src/dash/utils/roles';
import { highlight, languages } from '~/src/prismjs';

const NETWORK_SOURCE_PILLS: IPillItem[] = [
  {
    text: networkDemandSourceTypeToString(NetworkDemandSourceType.TYPE_PUBLICA),
  },
  {
    text: networkDemandSourceTypeToString(NetworkDemandSourceType.TYPE_VAST),
    activeTextColor: '#b660f5',
    activeBackgroundColor: '#efe2f8',
  },
];

interface IProps extends IWithStylesProps, IReduxActionProps, ISessionStateProps, IUserStateProps {
  // permissions
  canEdit: boolean;
  isUserInfoMasked: boolean;
  // other
  isNewOrClone?: boolean;
  // network channel params
  ID: number;
  SiteID: number;
  RouteID: number;
  Name: string;
  Weight: number;
  DemandSourceType: NetworkDemandSourceType;
  AdURL: string;
  AppBundleOverride: string;
  // index of channel split shown
  index: number;
  // total weight of all channels
  totalWeight: number;
  // once used has tried to save once, the error displays will appear
  saveAttempted: boolean;
  // handlers
  handleDeleteNetworkChannel: (idx: number) => void;
  handleChangeNetworkChannel: (idx: number, field: NetworkChannelField, value: any) => void;
  validateNetworkChannel: (channel: INetworkChannel) => string[];
  lvcStreamID: string;
}

interface IState {
  siteList: IMenuList;
  routeList: IMenuList;

  refreshingLiveLogs: boolean;
  refreshingLiveLogsTimestamp: string;

  // live logs data
  podRequestLiveLogs: IStreamPodRequestData[];
  podFilledLiveLogs: IStreamPodFilledData[];
  adFilledLiveLogs: IStreamAdFilledData[];
  liveStreamWatcherAdBreakLiveLogs: ILiveStreamWatcherAdBreakData[];
}

export const NetworkInventorySplit = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      gridRow: {
        direction: 'row',
        justify: 'flex-start',
        alignItems: 'center',
        marginBottom: 10,
      },
      gridRowURL: {
        width: '100%',
        marginTop: 15,
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    state = {
      siteList: {} as IMenuList,
      routeList: {} as IMenuList,
      podRequestLiveLogs: [] as IStreamPodRequestData[],
      podFilledLiveLogs: [] as IStreamPodFilledData[],
      adFilledLiveLogs: [] as IStreamAdFilledData[],
      liveStreamWatcherAdBreakLiveLogs: [] as ILiveStreamWatcherAdBreakData[],
      refreshingLiveLogs: false,
      refreshingLiveLogsTimestamp: '',
    };

    // NOTE: It is preferable to use helper functions in componentDidMount to keep it clean
    componentDidMount() {
      this.generateSiteList();
      this.generateRouteList();
      this.getLiveLogs();
    }

    componentDidUpdate(prevProps: IProps) {
      // Check if SiteID changed
      if (prevProps.SiteID !== this.props.SiteID) {
        this.generateSiteList();
      }
    }

    // -------- INITIALIZATION FUNCTIONS -------- //

    async generateSiteList() {
      const siteList: IMenuList = {};

      const [small_sites, err] = await getSmallSites(this.props.session.publisher.ID);
      if (err) {
        console.error(err);
        return;
      }

      (small_sites || []).forEach((site: ISmallSite) => {
        const siteName = this.props.isUserInfoMasked ? `demo_site_${site.ID}` : site.Name;
        siteList[site.ID] = {
          title: siteName,
          selected: site.ID === this.props.SiteID,
          ID: site.ID.toString(),
        };
      });

      this.setState({
        siteList,
      });
    }

    generateRouteList(): void {
      getInventoryRoutesByPublisherID(this.props.session.publisher.ID).then(
        (routes: IInventoryRoute[]) => {
          const maskedRoutes = routes.map((route: IInventoryRoute) => {
            // mask the site name for channel destinations
            route.Destinations = route.Destinations.map((destination: IDestination) => {
              destination.Name = checkUserInfoMasked(this.props.user.user)
                ? `demo_destination_${destination.ID}`
                : destination.Name;
              return destination;
            });
            return route;
          });

          const routeList: IMenuList = {};
          maskedRoutes.forEach((route: IInventoryRoute) => {
            routeList[route.ID] = {
              title: route.Name,
              selected: route.ID === this.props.RouteID,
              ID: route.ID.toString(),
            };
          });

          this.setState({
            routeList,
          });
        },
      );
    }

    getLiveLogs(): void {
      if (this.props.isNewOrClone) {
        return;
      }

      this.setState(
        {
          refreshingLiveLogs: true,
        },
        () => {
          GetNetworkChannelLiveLogsData(
            this.props.ID,
            this.props.session.publisher.ID,
            this.props.user.user.Username,
            this.props.lvcStreamID,
          ).then((data: INetworkChannelLiveLogsWrapper) => {
            this.setState({
              podRequestLiveLogs: data.podRequest,
              podFilledLiveLogs: data.podFilled,
              adFilledLiveLogs: data.adFilled,
              liveStreamWatcherAdBreakLiveLogs: data.liveStreamWatcherAdBreak,
              refreshingLiveLogs: false,
              refreshingLiveLogsTimestamp: formatTime(moment(), this.props.user.publisher.Timezone),
            });
          });
        },
      );
    }
    // -------- COMPONENT HELPER FUNCTIONS -------- //

    // determines the allocation percentage from totalWeight of all channels
    computeAllocation = (): string => {
      if (this.props.Weight === 0 || this.props.Weight === undefined) {
        return '0';
      }
      return ((this.props.Weight * 100) / this.props.totalWeight).toFixed(0);
    };

    // -------- HANDLER FUNCTIONS -------- //
    // NOTE: functions passed into a child component which update state in the parent
    // should be called handleFunc and used to not directly modify the props/state

    // updates site list and calls handler
    handleChannelAcceptClick = (siteList: IMenuList): void => {
      for (const item of Object.values(siteList)) {
        if (item.selected) {
          this.props.handleChangeNetworkChannel(this.props.index, 'SiteID', parseInt(item.ID, 10));
          break;
        }
      }
      this.setState({
        siteList,
      });
    };

    // Updates route list and calls handler
    handleRouteAcceptClick = (routeList: IMenuList): void => {
      for (const item of Object.values(routeList)) {
        if (item.selected) {
          this.props.handleChangeNetworkChannel(this.props.index, 'RouteID', parseInt(item.ID, 10));
          break;
        }
      }
      this.setState({
        routeList,
      });
    };

    renderLiveLogs = (): ReactElement => {
      const {
        adFilledLiveLogs,
        podFilledLiveLogs,
        podRequestLiveLogs,
        liveStreamWatcherAdBreakLiveLogs,
        refreshingLiveLogs,
        refreshingLiveLogsTimestamp,
      } = this.state;

      if (this.props.isNewOrClone) {
        return <></>;
      }

      const liveLogsElements = [
        {
          title: 'Pod Request',
          render: (searchFilter: string) => {
            return (
              <LiveLogsGeneralTable<IStreamPodRequestData>
                publisherTimezone={this.props.session.publisher.Timezone}
                data={filterLiveLogs(podRequestLiveLogs, searchFilter)}
                columns={[
                  {
                    title: 'Timestamp',
                    field: 'timestamp',
                    searchable: false,
                    width: '40px',
                    cellStyle: { whiteSpace: 'pre-wrap' },
                  },
                  {
                    title: 'Request URL',
                    field: 'requestURL',
                    searchable: false,
                    cellStyle: { maxWidth: 1200 },
                  },
                ]}
              />
            );
          },
        },
        {
          title: 'Pod Filled',
          render: (searchFilter: string) => {
            return (
              <LiveLogsGeneralTable<IStreamPodFilledData>
                publisherTimezone={this.props.session.publisher.Timezone}
                data={filterLiveLogs(podFilledLiveLogs, searchFilter)}
                columns={[
                  {
                    title: 'Timestamp',
                    field: 'timestamp',
                    searchable: false,
                    width: '40px',
                    cellStyle: { whiteSpace: 'pre-wrap' },
                  },
                  {
                    title: 'Request URL',
                    field: 'requestURL',
                    searchable: false,
                    cellStyle: { maxWidth: 1200 },
                  },
                ]}
                nestedData={[
                  {
                    title: 'Vast Response',
                    getData: (row: IStreamPodFilledData) => row.vastResponses,
                    getDataToCopy: (row: IStreamPodFilledData) => row.vastResponses,
                    render: (vastResponse: string): React.ReactElement => {
                      return (
                        <>
                          {vastResponse.split('|').map((resp: string) => {
                            return <XMLViewer xml={resp} invalidXml={<p>{resp}</p>} />;
                          })}
                        </>
                      );
                    },
                  },
                ]}
              />
            );
          },
        },
        {
          title: 'Ad Filled',
          render: (searchFilter: string) => {
            return (
              <LiveLogsGeneralTable<IStreamAdFilledData>
                publisherTimezone={this.props.session.publisher.Timezone}
                data={filterLiveLogs(adFilledLiveLogs, searchFilter)}
                columns={[
                  {
                    title: 'Timestamp',
                    field: 'timestamp',
                    searchable: false,
                    width: '40px',
                    cellStyle: { whiteSpace: 'pre-wrap' },
                  },
                  {
                    title: 'Request URL',
                    field: 'requestURL',
                    searchable: false,
                    cellStyle: { maxWidth: 1200 },
                  },
                ]}
                nestedData={[
                  {
                    title: 'M3u8 Urls',
                    getData: (row: IStreamAdFilledData) => {
                      const out = [] as Array<string | React.ReactElement>;
                      row.m3u8Urls.forEach((u: string) => {
                        out.push(u);
                        out.push(<br />);
                      });
                      return out;
                    },
                    getDataToCopy: (row: IStreamAdFilledData) => row.m3u8Urls.join(', '),
                    preFormat: 'text',
                  },
                ]}
              />
            );
          },
        },
      ];

      if (isSuper(this.props.user.user)) {
        liveLogsElements.push({
          title: 'Live Context Ad Break *',
          render: (searchFilter: string) => {
            return (
              <LiveContextAdBreakTable
                data={filterLiveLogs(liveStreamWatcherAdBreakLiveLogs, searchFilter)}
              />
            );
          },
        });
      }

      return (
        <LiveLogsWrapper
          useV2
          buttonText='Logs'
          isFixedButton={false}
          classes={{}}
          getLiveLogs={() => this.getLiveLogs()}
          refreshingLiveLogs={refreshingLiveLogs}
          refreshingLiveLogsTimestamp={refreshingLiveLogsTimestamp}
          elements={liveLogsElements}
        />
      );
    };

    // Renders channel split info for publica type network channels
    renderPublica = (): ReactElement => {
      const { AppBundleOverride, handleChangeNetworkChannel, index } = this.props;
      const { siteList } = this.state;

      return (
        <>
          <InputGridRow text='Channel'>
            <MaterialSearchDropdownMulti
              width='450px'
              fontSize='16px'
              menuTitle='Channels'
              selectAll={false}
              enableMultiSelect={false}
              list={siteList}
              onChange={(list: IMenuList) => {
                this.setState({
                  siteList: list,
                });
              }}
              handleMenuAcceptClick={this.handleChannelAcceptClick}
              maxHeight={350}
              showPrefixID
            />
          </InputGridRow>

          <InputGridRow text='Appbundle Override' preText='Optional'>
            <OutlinedInput
              value={AppBundleOverride}
              placeholder='Enter appbundle to pass in request'
              margin='dense'
              onChange={(changeEvent: React.ChangeEvent<{ value: unknown }>) => {
                handleChangeNetworkChannel(index, 'AppBundleOverride', changeEvent.target.value);
              }}
              style={{
                width: '450px',
              }}
            />
          </InputGridRow>
        </>
      );
    };

    // Renders channel split info for vast type network channels
    renderVast = (): ReactElement => {
      const { index, canEdit, AdURL, handleChangeNetworkChannel } = this.props;
      return (
        <InputGridRow
          text='VAST Url'
          subText='Replace macros to dynamically populate the values'
          style={{
            marginBottom: 0,
          }}
        >
          <Grid
            style={{
              background: canEdit ? 'aliceblue' : 'rgba(203,203,203,0.31)',
              borderRadius: smallRadius,
              opacity: '1',
              paddingLeft: '10px',
              paddingRight: '10px',
              width: '100%',
            }}
          >
            <VastEditor
              url={AdURL}
              urlID={index}
              highlight={(code) => highlight(code, languages.elm, 'elm')}
              padding={5}
              maxLength={2000}
              placeholder={'Enter VAST Url'}
              onURLChange={(adURL: string) => {
                if (canEdit) {
                  handleChangeNetworkChannel(index, 'AdURL', adURL);
                }
              }}
              style={{
                minHeight: '4em',
              }}
              textStyle={{
                background: 'aliceblue',
                borderRadius: smallRadius,
                opacity: '1',
                padding: '5px 10px',
              }}
              wikiMsgHidden
            />
          </Grid>
        </InputGridRow>
      );
    };

    // Renders channel split info for route type network channels
    renderRoute = (): ReactElement => {
      const { routeList } = this.state;
      return (
        <Grid container spacing={2} style={{ paddingTop: '10px' }}>
          {/* Route Dropdown */}
          <Grid item container direction='row' alignItems='center'>
            <Grid item xs={3}>
              <InputLabel>Route</InputLabel>
            </Grid>
            <Grid item xs={3}>
              <MaterialSearchDropdownMulti
                width='450px'
                fontSize='16px'
                menuTitle='Routes'
                selectAll={false}
                enableMultiSelect={false}
                list={routeList}
                onChange={(list: IMenuList) => {
                  this.setState({
                    routeList: list,
                  });
                }}
                handleMenuAcceptClick={this.handleRouteAcceptClick}
                maxHeight={350}
              />
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderDemandSourceType = (): ReactElement => {
      switch (this.props.DemandSourceType) {
        case NetworkDemandSourceType.TYPE_PUBLICA:
          return this.renderPublica();
        case NetworkDemandSourceType.TYPE_VAST:
          return this.renderVast();
        case NetworkDemandSourceType.TYPE_ROUTE:
          return this.renderRoute();
        default:
          return this.renderVast();
      }
    };

    render() {
      const {
        ID,
        canEdit,
        handleDeleteNetworkChannel,
        handleChangeNetworkChannel,
        index,
        Name,
        Weight,
        AdURL,
        DemandSourceType,
        SiteID,
        AppBundleOverride,
        RouteID,
        saveAttempted,
        validateNetworkChannel,
      } = this.props;

      const channel = {
        ID: 0,
        NetworkID: 0,
        Name,
        AdURL,
        AppBundleOverride,
        Weight,
        DemandSourceType,
        SiteID,
        RouteID,
      };
      const validationErrors = validateNetworkChannel(channel);

      return (
        <Grid item style={{ width: '100%' }}>
          <ModelPrimaryContainerV2
            style={{
              maxWidth: '80%',
            }}
          >
            {/* Inventory Split Name */}
            <InputGridRow
              text='Name'
              subText={ID !== 0 ? 'ID ' + ID : ''}
              endComponent={
                <Grid item>
                  {DemandSourceType !== NetworkDemandSourceType.TYPE_ROUTE && this.renderLiveLogs()}
                  <Button disabled={!canEdit} onClick={() => handleDeleteNetworkChannel(index)}>
                    <DeleteIcon />
                  </Button>
                </Grid>
              }
            >
              <OutlinedInput
                value={Name}
                disabled={!canEdit}
                error={Name.trim() === ''}
                placeholder='Enter a custom name'
                margin='dense'
                onChange={(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                  if (canEdit) {
                    handleChangeNetworkChannel(index, 'Name', event.target.value);
                  }
                }}
                style={{
                  width: '450px',
                }}
              />
            </InputGridRow>

            {/* Demand Source */}
            <InputGridRow
              text='Destination'
              style={{
                marginTop: '15px',
              }}
            >
              <PillSwitcher
                disabled={!canEdit}
                currentText={networkDemandSourceTypeToString(DemandSourceType)}
                pills={NETWORK_SOURCE_PILLS}
                onChange={(value: string) => {
                  handleChangeNetworkChannel(
                    index,
                    'DemandSourceType',
                    demandSourceStringToDemandSourceType(value),
                  );
                }}
              />
            </InputGridRow>

            {/* Demand Source Type Render */}
            <Grid item>{this.renderDemandSourceType()}</Grid>

            {/* Validation Errors */}
            {saveAttempted && validationErrors.length > 0 && (
              <Grid item>
                <Typography style={{ fontSize: 14, fontWeight: 500, color: 'red' }}>
                  {validationErrors[0]}
                </Typography>
              </Grid>
            )}

            <Divider
              style={{
                marginTop: '20px',
                marginBottom: '20px',
              }}
            />

            {/* Allocation - Weight */}
            <InputGridRow text='Allocation'>
              <OutlinedInput
                disabled={!canEdit}
                value={Weight}
                placeholder='Enter a number'
                margin='dense'
                onChange={(changeEvent: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                  handleChangeNetworkChannel(
                    index,
                    'Weight',
                    parseInt(changeEvent.target.value, 10) || 0,
                  );
                }}
              />
              <Typography style={{ fontSize: 14 }}>
                This route will get&nbsp;
                <strong>{this.computeAllocation()}</strong>% of the request from this network.
              </Typography>
            </InputGridRow>
          </ModelPrimaryContainerV2>
        </Grid>
      );
    }
  },
);
